import { useAuth } from "@clerk/clerk-react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  OrganizationNicheStatus,
  ProfileType,
  WarmupConfigReason,
} from "schemas/dashboard";
import { CustomEvent, SetAlertType } from "schemas/functions";

import Drawer from "components/Drawer";
import { fetcherAuth } from "utils/api";
import { titleCase } from "utils/string";
import { makeDeepCopy } from "utils/updateLocalState";

import AdminLoginAs from "../Organization/LoginAs";
import BrandSearchDialog from "./BrandSearchDialog";

interface AddressComponent {
  longText: string;
  shortText: string;
  types: string[];
}

interface SearchFilter {
  id: number;
  name: string;
}

interface VAUserDetails {
  organization: {
    id: number;
    name: string;
    vaStatus: string;
    type: string;
    createdAt: string | null;
  };
  user: {
    id: number;
    name: string;
    email: string;
  } | null;
  integration: {
    id: number;
    provider: string;
    email: string;
    integrationError: string | null;
    refreshRequired: boolean;
    outreachPaused: boolean;
  } | null;
  profile: {
    addressComponents: AddressComponent[] | null;
    secondaryLocations: { address_components?: AddressComponent[] }[] | null;
    city: string | null;
    location: string | null;
    isUgcCreator: boolean | null;
    isInfluencer: boolean | null;
    otherType: string | null;
    profileTypes?: ProfileType[];
  } | null;
  vaProfile: {
    onboardingType: string | null;
    personalizeOutreach: boolean;
    emailWarmupEnabled: boolean;
    openToAffiliates: string | null;
    openToGifted: string | null;
    openToForms: string | null;
    openToSocial: string | null;
  } | null;
  warmupConfig: {
    cycle: { emailVolume: number; days: number }[];
    currentStepIndex: number;
    startedAt: number | null;
    lastAdvancedAt: number | null;
    lastCompletedAt: number | null;
    reason: string;
  } | null;
  bulkSendSettingId: number | null;
  numberOfOverrideBrands: number | null;
  categories: { id: number; name: string }[];
  includedSearchFilters: SearchFilter[];
  excludedSearchFilters: SearchFilter[];
  subscription: {
    status: string;
    planName: string | null;
    createdAt: string | null;
  } | null;
}

interface AutomationStat {
  weekStartDate: string;
  totalBrandCount: number;
  cashBrandCount: number;
  cashOnlyBrandCount: number;
  affiliateBrandCount: number;
  productsOrServicesBrandCount: number;
  neverContactedBrandCount: number;
  hasDirectContactBrandCount: number;
  goodResponseRankingBrandCount: number;
  failedValidationBrandCount: number;
  sameCountryBrandCount: number | null;
  lowNichenessScoreBrandCount: number | null;
  mediumNichenessScoreBrandCount: number | null;
  highNichenessScoreBrandCount: number | null;
}

interface NicheTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  contentType: string;
}

interface NicheFollowUpResponse {
  question: string | null;
  selectedOptions: { option: string }[];
}

interface NicheAutomation {
  id: number;
  status: string;
  isTopNiche: boolean;
  searchParameters: Record<string, unknown> | null;
  templates: NicheTemplate[];
}

interface Niche {
  id: number;
  name: string;
  status: string;
  isArchived: boolean;
  locationName: string | null;
  templates: NicheTemplate[];
  followUpResponses: NicheFollowUpResponse[];
  automations: NicheAutomation[];
}

const formatLocation = (
  addressComponents?: AddressComponent[] | null,
): string => {
  if (!addressComponents) return "";
  const neighborhood = addressComponents.find((x) =>
    x.types?.includes("neighborhood"),
  )?.longText;
  const locality = addressComponents.find(
    (x) =>
      x.types?.includes("locality") ||
      x.types?.includes("administrative_area_level_3"),
  )?.longText;
  const adminArea = addressComponents.find((x) =>
    x.types?.includes("administrative_area_level_1"),
  )?.shortText;
  const postalCode = addressComponents.find((x) =>
    x.types?.includes("postal_code"),
  )?.longText;
  const country = addressComponents.find((x) =>
    x.types?.includes("country"),
  )?.longText;
  let text = neighborhood || "";
  if (locality) text += text ? `, ${locality}` : locality;
  if (adminArea) text += text ? `, ${adminArea}` : adminArea;
  if (postalCode) text += text ? ` ${postalCode}` : postalCode;
  if (country) text += text ? `, ${country}` : country;
  return text;
};

const COLLAB_LABELS: Record<string, string> = {
  openToAffiliates: "Affiliate Partnerships",
  openToGifted: "Gifted/Barter Partnerships",
  openToForms: "Brands That Require a Form",
  openToSocial: "Brands That Request Social Links",
};

const STATS_PER_PAGE = 5;

const DEFAULT_WARMUP_CYCLE = [
  { emailVolume: 50, days: 7 },
  { emailVolume: 65, days: 7 },
  { emailVolume: 75, days: 7 },
  { emailVolume: 100, days: 7 },
];

interface VAUserDrawerProps {
  onClose: () => void;
  setAlert: SetAlertType;
  categoriesById: Record<number, string>;
}

export default function VAUserDrawer({
  onClose,
  setAlert,
  categoriesById,
}: VAUserDrawerProps) {
  const { getToken } = useAuth();
  const { id } = useParams();
  const organizationId = id ? Number(id) : null;
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<VAUserDetails | null>(null);

  const [statsPage, setStatsPage] = useState(1);
  const [stats, setStats] = useState<AutomationStat[]>([]);
  const [statsTotal, setStatsTotal] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  const [niches, setNiches] = useState<Niche[]>([]);
  const [nichesLoading, setNichesLoading] = useState(false);
  const [expandedNicheId, setExpandedNicheId] = useState<number | null>(null);
  const [templateDialog, setTemplateDialog] = useState<NicheTemplate | null>(
    null,
  );
  const [brandSearchOpen, setBrandSearchOpen] = useState(false);

  // Warmup management state
  const [warmupEnabled, setWarmupEnabled] = useState(false);
  const [warmupEnabledSaving, setWarmupEnabledSaving] = useState(false);
  const [warmupReason, setWarmupReason] = useState<WarmupConfigReason | null>(
    null,
  );
  const [warmupCurrentStep, setWarmupCurrentStep] = useState(0);
  const [warmupCycle, setWarmupCycle] = useState<
    { emailVolume: number; days: number }[]
  >([...DEFAULT_WARMUP_CYCLE]);
  const [warmupConfigSaving, setWarmupConfigSaving] = useState(false);
  const [numberOfOverrideBrandsValue, setNumberOfOverrideBrandsValue] =
    useState<string>("");
  const [overrideSaving, setOverrideSaving] = useState(false);

  const activeNiches = niches.filter(
    (n) => !n.isArchived && n.status === OrganizationNicheStatus.active,
  );

  useEffect(() => {
    if (organizationId) {
      fetchDetails(organizationId);
      setStatsPage(1);
      fetchStats(organizationId, 1);
      fetchNiches(organizationId);
    } else {
      setDetails(null);
      setStats([]);
      setStatsTotal(0);
      setNiches([]);
      setExpandedNicheId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    if (details) {
      setWarmupEnabled(details.vaProfile?.emailWarmupEnabled ?? false);
      setNumberOfOverrideBrandsValue(
        details.numberOfOverrideBrands != null
          ? String(details.numberOfOverrideBrands)
          : "",
      );
      if (details.warmupConfig) {
        setWarmupReason(details.warmupConfig.reason as WarmupConfigReason);
        setWarmupCurrentStep(details.warmupConfig.currentStepIndex ?? 0);
        setWarmupCycle(
          details.warmupConfig.cycle?.length
            ? details.warmupConfig.cycle
            : [...DEFAULT_WARMUP_CYCLE],
        );
      } else {
        setWarmupReason(null);
        setWarmupCurrentStep(0);
        setWarmupCycle([...DEFAULT_WARMUP_CYCLE]);
      }
    }
  }, [details]);

  const fetchDetails = async (orgId: number) => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${orgId}/details`,
        "GET",
      );
      setDetails(res);
    } catch (error: any) {
      setAlert(error?.message || "Failed to load user details", "warning");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (orgId: number, page: number) => {
    setStatsLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${orgId}/automation-stats?page=${page}&per_page=${STATS_PER_PAGE}`,
        "GET",
      );
      setStats(res.stats);
      setStatsTotal(res.total);
    } catch (error: any) {
      setAlert(error?.message || "Failed to load automation stats", "warning");
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchNiches = async (orgId: number) => {
    setNichesLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${orgId}/niches`,
        "GET",
      );
      setNiches(res.niches);
    } catch (error: any) {
      setAlert(error?.message || "Failed to load niches", "warning");
    } finally {
      setNichesLoading(false);
    }
  };

  const handleToggleWarmupEnabled = async (enabled: boolean) => {
    setWarmupEnabled(enabled);
    setWarmupEnabledSaving(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${organizationId}/warmup-settings`,
        "PATCH",
        {},
        { emailWarmupEnabled: enabled },
      );
      setAlert("Updated settings", "success");
      setDetails((prev) => {
        if (!prev) return prev;
        const copy = makeDeepCopy(prev);
        copy.vaProfile.emailWarmupEnabled = enabled;
        return copy;
      });
    } catch (error: any) {
      setAlert(error?.message || "Failed to update warmup enabled", "warning");
      setWarmupEnabled(!enabled);
    } finally {
      setWarmupEnabledSaving(false);
    }
  };

  const handleSaveWarmupConfig = async () => {
    setWarmupConfigSaving(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${organizationId}/warmup-settings`,
        "PATCH",
        {},
        {
          cycle: warmupCycle,
          currentStepIndex: warmupCurrentStep,
          reason: warmupReason,
        },
      );
      setAlert("Warmup config saved", "success");
      if (organizationId) {
        fetchDetails(organizationId);
      }
    } catch (error: any) {
      setAlert(error?.message || "Failed to save warmup config", "warning");
    } finally {
      setWarmupConfigSaving(false);
    }
  };

  const handleSavenumberOfOverrideBrands = async () => {
    setOverrideSaving(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${organizationId}/warmup-settings`,
        "PATCH",
        {},
        {
          numberOfOverrideBrands:
            numberOfOverrideBrandsValue === ""
              ? 0
              : Number(numberOfOverrideBrandsValue),
        },
      );
      setAlert("Override saved", "success");
    } catch (error: any) {
      setAlert(error?.message || "Failed to save override", "warning");
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleStatsPageChange = (_: CustomEvent, newPage: number) => {
    const page = newPage + 1;
    setStatsPage(page);
    if (organizationId) {
      fetchStats(organizationId, page);
    }
  };

  const pct = (count: number | null, total: number) => {
    if (!total || count == null) return "-";
    return `${Math.round((count / total) * 100)}%`;
  };

  const getEmailConnectionStatus = () => {
    if (!details?.integration) {
      return { label: "No Email Connected", color: "error" as const };
    }
    if (
      details.integration.integrationError ||
      details.integration.refreshRequired
    ) {
      return { label: "Disconnected", color: "error" as const };
    }
    if (details.integration.outreachPaused) {
      return { label: "Paused", color: "warning" as const };
    }
    return { label: "Connected", color: "success" as const };
  };

  const emailStatus = details ? getEmailConnectionStatus() : null;

  return (
    <Drawer
      open={!!organizationId}
      onClose={onClose}
      width={{ xs: "auto", md: "90%", lg: "80%" }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Button onClick={onClose}>Close</Button>
          <Typography variant="h6">
            {details?.organization?.name || `Org ${organizationId}`}
          </Typography>
          <Box sx={{ width: 64 }} />
        </Grid>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : details ? (
          <Grid container direction="column" spacing={3}>
            {/* Email Connection Status */}
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                Email Connection
              </Typography>
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Chip
                  label={emailStatus?.label}
                  color={emailStatus?.color}
                  size="small"
                />
                {details.integration?.email && (
                  <Typography variant="body2">
                    {details.integration.email}
                  </Typography>
                )}
              </Box>
              {details.integration?.integrationError && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  Error: {details.integration.integrationError}
                </Typography>
              )}
            </Grid>

            <Grid item>
              <Divider />
            </Grid>

            {/* Organization Info */}
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                Organization
              </Typography>
              <Box sx={{ mt: 1 }}>
                <InfoRow label="Org ID" value={details.organization.id} />
                <InfoRow label="Name" value={details.organization.name} />
                <InfoRow label="Type" value={details.organization.type} />
                <InfoRow
                  label="VA Status"
                  value={details.organization.vaStatus}
                />
                <InfoRow
                  label="Created"
                  value={
                    details.organization.createdAt
                      ? new Date(
                          details.organization.createdAt,
                        ).toLocaleDateString()
                      : "-"
                  }
                />
              </Box>
            </Grid>

            {/* User Info */}
            {details.user && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  User
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <InfoRow label="User ID" value={details.user.id} />
                  <InfoRow label="Name" value={details.user.name} />
                  <InfoRow label="Email" value={details.user.email} />
                </Box>
              </Grid>
            )}

            {/* Subscription Info */}
            {details.subscription && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  Subscription
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <InfoRow label="Status" value={details.subscription.status} />
                  <InfoRow label="Plan" value={details.subscription.planName} />
                  <InfoRow
                    label="Since"
                    value={
                      details.subscription.createdAt
                        ? new Date(
                            details.subscription.createdAt,
                          ).toLocaleDateString()
                        : "-"
                    }
                  />
                </Box>
              </Grid>
            )}

            {/* Location */}
            {details.profile && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  Location
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {formatLocation(details.profile.addressComponents) || "-"}
                  </Typography>
                  {details.profile.secondaryLocations &&
                    details.profile.secondaryLocations.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Secondary Locations
                        </Typography>
                        {details.profile.secondaryLocations.map((loc, i) => (
                          <Typography key={i} variant="body2">
                            {formatLocation(loc.address_components) || "-"}
                          </Typography>
                        ))}
                      </Box>
                    )}
                </Box>
              </Grid>
            )}

            {/* Creator Type */}
            {details.profile && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  Creator Type
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {details.profile.otherType && (
                    <Chip
                      label={details.profile.otherType}
                      size="small"
                      color="primary"
                    />
                  )}
                  {details?.profile?.profileTypes &&
                    details.profile.profileTypes.map((pt) => (
                      <Chip key={pt} label={pt} size="small" color="primary" />
                    ))}

                  {!details.profile.isUgcCreator &&
                    !details.profile.isInfluencer &&
                    !details.profile.otherType &&
                    !details?.profile?.profileTypes && (
                      <Typography variant="body2" color="text.secondary">
                        Not set
                      </Typography>
                    )}
                </Box>
              </Grid>
            )}

            {/* Categories */}
            {details.categories && details.categories.length > 0 && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  Categories
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {details.categories.map((cat) => (
                    <Chip
                      key={cat.id}
                      label={categoriesById[cat.id] || cat.name}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Search Filters */}
            {(details.includedSearchFilters.length > 0 ||
              details.excludedSearchFilters.length > 0) && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  Search Filters
                </Typography>
                {details.includedSearchFilters.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Included
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {details.includedSearchFilters.map((f) => (
                        <Chip
                          key={f.id}
                          label={f.name}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {details.excludedSearchFilters.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Excluded
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {details.excludedSearchFilters.map((f) => (
                        <Chip
                          key={f.id}
                          label={f.name}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>
            )}

            {/* Collab Preferences */}
            {details.vaProfile && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  Collab Preferences
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {(
                    [
                      "openToAffiliates",
                      "openToGifted",
                      "openToForms",
                      "openToSocial",
                    ] as const
                  ).map((key) => (
                    <InfoRow
                      key={key}
                      label={COLLAB_LABELS[key]}
                      value={titleCase(details.vaProfile?.[key] || "-")}
                    />
                  ))}
                  <InfoRow
                    label="Personalize Outreach"
                    value={details.vaProfile.personalizeOutreach ? "Yes" : "No"}
                  />
                </Box>
              </Grid>
            )}

            <Grid item>
              <Divider />
            </Grid>

            {/* Login As */}
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Actions
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <AdminLoginAs
                  organizationId={details.organization.id}
                  setAlert={setAlert}
                />
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={activeNiches.length === 0}
                  sx={{ mt: 2 }}
                  onClick={() => setBrandSearchOpen(true)}
                >
                  Search Brands
                </Button>
              </Box>
            </Grid>

            <Grid item>
              <Divider />
            </Grid>

            {/* Email Warmup */}
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Email Warmup
              </Typography>

              {/* Override number of brands */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 2,
                }}
              >
                <TextField
                  label="Override # of Brands"
                  type="number"
                  size="small"
                  value={numberOfOverrideBrandsValue}
                  onChange={(e) =>
                    setNumberOfOverrideBrandsValue(e.target.value)
                  }
                  inputProps={{ min: 0 }}
                  helperText="Override the user's total number of brands to be sent (does not include add-ons)."
                  sx={{ width: 220 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSavenumberOfOverrideBrands}
                  disabled={overrideSaving}
                  sx={{ mt: 0.5 }}
                >
                  {overrideSaving ? "Saving..." : "Set"}
                </Button>
                {Number(numberOfOverrideBrandsValue) > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      setOverrideSaving(true);
                      try {
                        await fetcherAuth(
                          getToken,
                          `/api/admin/virtual-assistant-users/${organizationId}/warmup-settings`,
                          "PATCH",
                          {},
                          { numberOfOverrideBrands: 0 },
                        );
                        setNumberOfOverrideBrandsValue("");
                        setAlert("Override cleared", "success");
                        if (organizationId) fetchDetails(organizationId);
                      } catch (error: any) {
                        setAlert(
                          error?.message || "Failed to clear override",
                          "warning",
                        );
                      } finally {
                        setOverrideSaving(false);
                      }
                    }}
                    disabled={overrideSaving}
                    color="warning"
                    sx={{ mt: 0.5 }}
                  >
                    Unset
                  </Button>
                )}
              </Box>

              {numberOfOverrideBrandsValue !== "" && (
                <Alert severity="warning" sx={{ mb: 2, width: "max-content" }}>
                  The override number of brands is currently set to{" "}
                  {numberOfOverrideBrandsValue}. Warmup schedule is current{" "}
                  <strong>NOT</strong> in use.
                </Alert>
              )}

              {/* Toggle */}

              <Box
                sx={{ opacity: numberOfOverrideBrandsValue !== "" ? 0.5 : 1 }}
              >
                {" "}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={warmupEnabled}
                        onChange={(e) =>
                          handleToggleWarmupEnabled(e.target.checked)
                        }
                        disabled={
                          warmupEnabledSaving ||
                          numberOfOverrideBrandsValue !== ""
                        }
                      />
                    }
                    label="Email Warmup Enabled"
                  />
                  {warmupEnabledSaving && <CircularProgress size={16} />}
                </Box>
                {/* Current config info (read-only) */}
                {details?.warmupConfig && (
                  <Box sx={{ mb: 2 }}>
                    <InfoRow
                      label="Current Step"
                      value={`${(details.warmupConfig.currentStepIndex ?? 0) + 1} of ${details.warmupConfig.cycle?.length ?? 0}`}
                    />
                    {details.warmupConfig.startedAt && (
                      <InfoRow
                        label="Started"
                        value={new Date(
                          details.warmupConfig.startedAt * 1000,
                        ).toLocaleDateString()}
                      />
                    )}
                    {details.warmupConfig.lastAdvancedAt && (
                      <InfoRow
                        label="Last Advanced"
                        value={new Date(
                          details.warmupConfig.lastAdvancedAt * 1000,
                        ).toLocaleDateString()}
                      />
                    )}
                    {details.warmupConfig.lastCompletedAt && (
                      <InfoRow
                        label="Last Completed"
                        value={new Date(
                          details.warmupConfig.lastCompletedAt * 1000,
                        ).toLocaleDateString()}
                      />
                    )}
                  </Box>
                )}
                {/* Editable config fields */}
                {details?.vaProfile?.emailWarmupEnabled && (
                  <>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <InputLabel>Reason</InputLabel>
                        <Select
                          value={warmupReason}
                          label="Reason"
                          onChange={(e) => {
                            if (e.target.value === "") {
                              setWarmupReason(null);
                            } else {
                              setWarmupReason(
                                e.target.value as WarmupConfigReason,
                              );
                            }
                          }}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          <MenuItem
                            value={WarmupConfigReason.new_email_address}
                          >
                            New Email Address
                          </MenuItem>
                          <MenuItem value={WarmupConfigReason.email_blocked}>
                            Email Blocked
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label={`Current Step Index (0 to ${(warmupCycle.length ?? 1) - 1})`}
                        type="number"
                        size="small"
                        value={warmupCurrentStep}
                        onChange={(e) =>
                          setWarmupCurrentStep(Number(e.target.value))
                        }
                        inputProps={{ min: 0 }}
                        helperText="Set to 0 will restart the warmup cycle from beginning and erase the previous `completed at` timestamp."
                      />

                      {/* Cycle step editor */}
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 2 }}
                        >
                          Warmup Cycle
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {warmupCycle.map((step, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ minWidth: 24 }}
                              >
                                {i + 1}.
                              </Typography>
                              <TextField
                                label="Email Volume"
                                type="number"
                                size="small"
                                value={step.emailVolume}
                                onChange={(e) => {
                                  const updated = [...warmupCycle];
                                  updated[i] = {
                                    ...updated[i],
                                    emailVolume: Number(e.target.value),
                                  };
                                  setWarmupCycle(updated);
                                }}
                                inputProps={{ min: 1 }}
                                sx={{ width: 140 }}
                              />
                              <TextField
                                label="Days"
                                type="number"
                                size="small"
                                value={step.days}
                                onChange={(e) => {
                                  const updated = [...warmupCycle];
                                  updated[i] = {
                                    ...updated[i],
                                    days: Number(e.target.value),
                                  };
                                  setWarmupCycle(updated);
                                }}
                                inputProps={{ min: 1 }}
                                sx={{ width: 90 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setWarmupCycle(
                                    warmupCycle.filter((_, j) => j !== i),
                                  )
                                }
                                disabled={warmupCycle.length <= 1}
                                sx={{ color: "error.main" }}
                              >
                                <Typography variant="body2">
                                  <Box
                                    component="i"
                                    className="fa-regular fa-trash"
                                  />
                                </Typography>
                              </IconButton>
                            </Box>
                          ))}
                          <Box>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setWarmupCycle([
                                  ...warmupCycle,
                                  { emailVolume: 100, days: 7 },
                                ])
                              }
                              sx={{ mt: 0.5 }}
                            >
                              + Add Step
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSaveWarmupConfig}
                        disabled={warmupConfigSaving}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {warmupConfigSaving ? "Saving..." : "Save Config"}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>

            <Grid item>
              <Divider />
            </Grid>

            {/* Weekly Automation Stats */}
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Weekly Automation Stats
              </Typography>
              {statsLoading ? (
                <Grid container justifyContent="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Grid>
              ) : stats.length > 0 ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Week</TableCell>
                          <TableCell align="right">Brands</TableCell>
                          <TableCell align="right">Cash</TableCell>
                          <TableCell align="right">Affiliate</TableCell>
                          <TableCell align="right">Products</TableCell>
                          <TableCell align="right">New</TableCell>
                          <TableCell align="right">Direct</TableCell>
                          <TableCell align="right">Good Resp.</TableCell>
                          <TableCell align="right">Failed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.map((row) => (
                          <TableRow key={row.weekStartDate}>
                            <TableCell>
                              {new Date(row.weekStartDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              {row.totalBrandCount}
                            </TableCell>
                            <TableCell align="right">
                              {pct(row.cashBrandCount, row.totalBrandCount)}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.affiliateBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.productsOrServicesBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.neverContactedBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.hasDirectContactBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.goodResponseRankingBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.failedValidationBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={statsTotal}
                    rowsPerPage={STATS_PER_PAGE}
                    rowsPerPageOptions={[STATS_PER_PAGE]}
                    page={statsPage - 1}
                    onPageChange={handleStatsPageChange}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No automation stats available.
                </Typography>
              )}
            </Grid>

            <Grid item>
              <Divider />
            </Grid>

            {/* Niches */}
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Niches
              </Typography>
              {nichesLoading ? (
                <Grid container justifyContent="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Grid>
              ) : niches.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {niches.map((niche) => (
                    <Box
                      key={niche.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      {/* Niche header — always visible */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1.5,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                        onClick={() =>
                          setExpandedNicheId(
                            expandedNicheId === niche.id ? null : niche.id,
                          )
                        }
                      >
                        <Chip label={niche.name || "Unnamed"} size="small" />
                        <Chip
                          label={
                            niche.isArchived
                              ? "archived"
                              : niche.status === "canceled"
                                ? "paused"
                                : niche.status
                          }
                          size="small"
                          color={
                            niche.isArchived
                              ? "default"
                              : niche.status === "active"
                                ? "success"
                                : niche.status === "canceled"
                                  ? "warning"
                                  : "default"
                          }
                          variant="outlined"
                        />
                        {niche.locationName && (
                          <Typography variant="body2" color="text.secondary">
                            {niche.locationName}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: "auto" }}
                        >
                          {niche.templates.length} template
                          {niche.templates.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>

                      {/* Expanded content */}
                      <Collapse in={expandedNicheId === niche.id}>
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                          {/* Niche-level templates */}
                          {niche.templates.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Templates
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  mt: 0.5,
                                }}
                              >
                                {niche.templates.map((t) => (
                                  <Chip
                                    key={t.id}
                                    label={t.name || t.contentType}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setTemplateDialog(t)}
                                    sx={{ cursor: "pointer" }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Follow-up responses */}
                          {niche.followUpResponses.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Follow-Up Responses
                              </Typography>
                              {niche.followUpResponses.map((resp, i) => (
                                <Box key={i} sx={{ mt: 0.5 }}>
                                  <Typography variant="body2">
                                    {resp.question}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      mt: 0.25,
                                    }}
                                  >
                                    {resp.selectedOptions.map((opt, j) => (
                                      <Chip
                                        key={j}
                                        label={opt.option}
                                        size="small"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Automations */}
                          {niche.automations.length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Automations
                              </Typography>
                              {niche.automations.map((auto) => (
                                <Box
                                  key={auto.id}
                                  sx={{
                                    mt: 0.5,
                                    p: 1,
                                    bgcolor: "action.hover",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Chip
                                      label={auto.status}
                                      size="small"
                                      color={
                                        auto.status === "active"
                                          ? "success"
                                          : "default"
                                      }
                                      variant="outlined"
                                    />
                                    {auto.isTopNiche && (
                                      <Chip
                                        label="Top Niche"
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>

                                  {/* Search parameters */}
                                  {auto.searchParameters && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        mb: 0.5,
                                      }}
                                    >
                                      {Object.entries(auto.searchParameters)
                                        .filter(
                                          ([, v]) =>
                                            v != null &&
                                            v !== "" &&
                                            !(
                                              Array.isArray(v) && v.length === 0
                                            ),
                                        )
                                        .flatMap(([key, val]) => {
                                          if (
                                            key === "category_ids" &&
                                            Array.isArray(val)
                                          ) {
                                            return val.map((id) => (
                                              <Chip
                                                key={`cat-${id}`}
                                                label={
                                                  categoriesById[Number(id)] ||
                                                  String(id)
                                                }
                                                size="small"
                                                sx={{
                                                  backgroundColor:
                                                    "primary.light",
                                                }}
                                              />
                                            ));
                                          }
                                          const display = Array.isArray(val)
                                            ? val.join(", ")
                                            : String(val);
                                          return (
                                            <Chip
                                              key={key}
                                              label={`${titleCase(
                                                key,
                                              )}: ${display}`}
                                              size="small"
                                              variant="outlined"
                                            />
                                          );
                                        })}
                                    </Box>
                                  )}

                                  {/* Automation templates */}
                                  {auto.templates.length > 0 && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                      }}
                                    >
                                      {auto.templates.map((t) => (
                                        <Chip
                                          key={t.id}
                                          label={t.name || t.contentType}
                                          size="small"
                                          variant="outlined"
                                          onClick={() => setTemplateDialog(t)}
                                          sx={{ cursor: "pointer" }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No niches available.
                </Typography>
              )}
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>

      {/* Template preview dialog */}
      <Dialog
        open={!!templateDialog}
        onClose={() => setTemplateDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        {templateDialog && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">
                  {templateDialog.name || "Template"}
                </Typography>
                <IconButton onClick={() => setTemplateDialog(null)}>
                  <Typography variant="body2">X</Typography>
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Subject
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {templateDialog.subject || "-"}
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Body
              </Typography>
              <Box
                sx={{
                  p: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
                dangerouslySetInnerHTML={{
                  __html: templateDialog.body || "-",
                }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>

      {organizationId && activeNiches.length > 0 && (
        <BrandSearchDialog
          open={brandSearchOpen}
          onClose={() => setBrandSearchOpen(false)}
          organizationId={organizationId}
          niches={activeNiches}
          setAlert={setAlert}
          includedSearchFilters={details?.includedSearchFilters || []}
          excludedSearchFilters={details?.excludedSearchFilters || []}
        />
      )}
    </Drawer>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <Grid container spacing={1} sx={{ mb: 0.5 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2">{value ?? "-"}</Typography>
      </Grid>
    </Grid>
  );
}
